import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { PRICE_UNDER_REVIEW } from '@/shared/lib/bookingPrice';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { classifyChannel } from '../../calendar/calendarHelpers';
import { labelFor, type PropertyLabel } from '../../properties/propertyLabels';
import type { BookingGroupEntry } from '../components/bookingsGrouping';
import { RESERVATION_STATUS_PILL } from '../components/reservationStatus';
import { BookingAgendaCard } from './BookingAgendaCard';
import { ChannelMark } from './agendaCardParts';

interface Props {
  group: BookingGroupEntry;
  propertyNames: ReadonlyMap<string, PropertyLabel>;
  canSeeGuests: boolean;
  today: string;
}

const STATUS_TONE = {
  PartiallyCancelled: 'bg-amber-tint text-ink',
  Cancelled: 'bg-sealwax-tint text-sealwax',
};

/**
 * Mehrzimmer-Reservierung: Kopf mit Kanal, Code, Zahl der Apartments und Summe, darunter die
 * Zimmer. Die Summe zählt nur die hier gelieferten Zimmer; über die Seite hinaus steht „Teilansicht“.
 */
export function BookingAgendaGroup({ group, propertyNames, canSeeGuests, today }: Props) {
  const [open, setOpen] = useState(true);
  const channel = classifyChannel(group.channel);
  const status =
    group.reservationStatus && group.reservationStatus !== 'Active'
      ? group.reservationStatus
      : null;
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'bg-card-alt flex flex-wrap items-center gap-2.5 px-4 py-2.5 text-left text-[13px]',
          open ? 'rounded-t-tile' : 'rounded-tile',
        )}
      >
        <ChevronDown
          size={14}
          aria-hidden
          className={cn('transition-transform', !open && '-rotate-90')}
        />
        <ChannelMark letter={channel.letter} className={cn(channel.bgClass, channel.textClass)} />
        <b>
          {channel.label}
          {group.reservationCode ? ` · ${group.reservationCode}` : ''} · {group.rows.length}{' '}
          {group.rows.length === 1 ? 'Apartment' : 'Apartments'}
        </b>
        {status && (
          <span
            className={cn(
              'rounded-pill inline-flex h-[22px] items-center px-2 text-[11px] font-bold',
              STATUS_TONE[status],
            )}
          >
            {RESERVATION_STATUS_PILL[status].label}
          </span>
        )}
        {group.isPartial && (
          <span className="text-ink-faint text-xs">
            Teilansicht – {group.visibleSiblingCount} auf mehreren Seiten
          </span>
        )}
        <span className="text-ink-soft ml-auto">
          zusammen{' '}
          <b className="text-ink">
            {group.visibleTotal === null
              ? PRICE_UNDER_REVIEW
              : formatMoney(group.visibleTotal, group.currency)}
          </b>
        </span>
      </button>
      {open &&
        group.rows.map((row, index) => (
          <BookingAgendaCard
            key={row.id}
            booking={row}
            apartment={labelFor(propertyNames, row.propertyId)}
            canSeeGuests={canSeeGuests}
            today={today}
            inGroup={index === group.rows.length - 1 ? 'last' : 'middle'}
          />
        ))}
    </div>
  );
}
